import React, { useMemo } from 'react';
import PropTypes from "prop-types";
import { Nav, NavItem, NavLink, TabContent, TabPane } from "reactstrap";
import MatchScoreEditTab from "./MatchScoreEditTab";
import CoverLetterEditTab from "./CoverLetterEditTab";
import AvailabilityRateEditTab from "./AvailabilityRateEditTab";
import { useQueryClient } from '@tanstack/react-query';
import Opportunity from "../../models/Opportunity";
import DocumentsEditTab from "./DocumentsEditTab";
import ManageOppMeetings from "../../components/meetings/ManageOppMeetings";
import MessagesTab from "./MessagesTab";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { Badge } from "antd";
import { useAuth } from '../../providers/AuthProvider';
import { useOpportunity } from "../../providers/OpportunityProvider";
import TeamScopeTab from "./TeamScopeTab";
import TeamMemberPayoutEditTab from "./TeamMemberPayoutEditTab";
import FixedFeePayoutEditTab from "./FixedFeePayoutEditTab";
import FixedRetainerPayoutEditTab from './FixedRetainerPayoutEditTab';

const OpportunityProposal = (props) => {

    const {
        opportunity,
        onUpdate,
        onRequest,
        changeProps,
        isDisabled,
    } = useOpportunity();
    const isIndividualOpp = opportunity.isInterested() || opportunity.isProposingAsIndividual();
    const isTeamOpp = opportunity.isProposingWithTeam();
    const [searchParams, setSearchParams] = useSearchParams();

    const queryClient = useQueryClient();
    const location = useLocation();
    const navigate = useNavigate();
    const { auth } = useAuth();

    const proposalTabs = useMemo(() => {
        let tabs = {
            "Team": isTeamOpp,
            "Role & Payout": isTeamOpp,
            "My Match Score": isIndividualOpp,
            "Cover Letter": true,
            "Payout": isIndividualOpp && opportunity.isFixedFee(),
            "Pricing": isIndividualOpp && opportunity.isFixedRetainer(),
            "Availability & Rate": isIndividualOpp && opportunity.isTimeMaterials(),
            "Supporting Documents": !opportunity.isInterested() && opportunity.hasSubmittedProposal(),
            "Interviews": opportunity.canMessageAndAcceptInterviews(),
            "Messages": opportunity.canMessageAndAcceptInterviews(),
        };


        return TB.reduce(tabs, (result, value, key) => {
            if (value) {
                result.push(key)
            }
            return result
        }, []);
    }, [isTeamOpp, isIndividualOpp, opportunity, isDisabled]);


    const selectedTab = useMemo(() => {

        let selected = TB.first(proposalTabs);
        if (searchParams.has('tabView')) {
            selected = TB.find(proposalTabs, tab => TB.slugify(tab) === searchParams.get('tabView'));
        }

        return selected;
    }, [proposalTabs, searchParams.get('tabView')]);

    const updateOpp = async (params) => {
        if (params) {
            let updateParams = { ...params };

            if (!opportunity.job_candidate?.talent_proposed_at) {
                updateParams.job_candidate = {
                    ...(opportunity.job_candidate || {}),
                    ...(updateParams.job_candidate || {}),
                    talent_proposed_at: new Date()
                };
                if (updateParams.job_candidate?.latest_proposal && !(updateParams.job_candidate?.latest_proposal instanceof Blob || updateParams.job_candidate?.latest_proposal instanceof File)) {
                    delete updateParams.job_candidate?.latest_proposal;
                }
            }

            let updatedOpp = await onUpdate(updateParams);

            if (updatedOpp) {
                return TB.sysAlert('talent.opportunity_proposal_save_btn_click')
            }
        }
    }

    const onSetActiveTab = (tab) => {
        let params = new URLSearchParams(searchParams);
        params.set('tabView', TB.slugify(tab))

        return setSearchParams(params)
    }

    const isSupplier = auth.isTalentSupplierResource();

    return (
        <div className="proposal-container opportunity-proposal">

            <div className="proposal-info">
                <Nav tabs className="proposal-tabs-section">
                    {isTeamOpp && (
                        <NavItem active={selectedTab === "Team"}>
                            <NavLink
                                className={TB.mapClass({
                                    clickable: selectedTab !== "Team",
                                    active: selectedTab === "Team"
                                })}
                                onClick={() => onSetActiveTab("Team")}>
                                Team
                            </NavLink>
                        </NavItem>
                    )}
                    {isTeamOpp && (
                        <NavItem active={selectedTab === "Role & Payout"}>
                            <NavLink
                                className={TB.mapClass({
                                    clickable: selectedTab !== "Role & Payout",
                                    active: selectedTab === "Role & Payout"
                                })}
                                onClick={() => onSetActiveTab("Role & Payout")}>
                                Role & Payout
                            </NavLink>
                        </NavItem>
                    )}

                    {isIndividualOpp && (
                        <NavItem active={selectedTab === "My Match Score"}>
                            <NavLink
                                className={TB.mapClass({
                                    clickable: selectedTab !== "My Match Score",
                                    active: selectedTab === "My Match Score"
                                })}
                                onClick={() => onSetActiveTab("My Match Score")}>
                                My Match Score
                            </NavLink>
                        </NavItem>
                    )}

                    {!isSupplier && (
                        <NavItem active={selectedTab === "Cover Letter"}>
                            <NavLink
                                className={TB.mapClass({
                                    clickable: selectedTab !== "Cover Letter",
                                    active: selectedTab === "Cover Letter"
                                })} onClick={() => onSetActiveTab("Cover Letter")}>
                                <span className="flex flex-row items-center gap-1.5">
                                    {!opportunity?.hasCompletedCoverLetter() && (
                                        <i className="fas text-danger fa-exclamation-circle" />
                                    )}
                                    <span>Cover Letter</span>
                                </span>
                            </NavLink>
                        </NavItem>
                    )}

                    {isIndividualOpp && !isSupplier && (
                        <>
                            {opportunity.isFixedRetainer() && (
                                <NavItem active={selectedTab === "Pricing"}>
                                    <NavLink className={TB.mapClass({
                                        clickable: selectedTab !== "Pricing",
                                        active: selectedTab === "Pricing",
                                    })} onClick={() => onSetActiveTab("Pricing")}>
                                        Pricing
                                    </NavLink>
                                </NavItem>
                            )}

                            {opportunity.isFixedFee() && (
                                <NavItem active={selectedTab === "Payout"}>
                                    <NavLink className={TB.mapClass({
                                        clickable: selectedTab !== "Payout",
                                        active: selectedTab === "Payout",
                                    })} onClick={() => onSetActiveTab("Payout")}>
                                        Payout
                                    </NavLink>
                                </NavItem>
                            )}

                            {opportunity.isTimeMaterials() && (
                                <NavItem active={selectedTab === "Availability & Rate"}>
                                    <NavLink className={TB.mapClass({
                                        clickable: selectedTab !== "Availability & Rate",
                                        active: selectedTab === "Availability & Rate",
                                    })} onClick={() => onSetActiveTab("Availability & Rate")}>
                                        Availability & Rate
                                    </NavLink>
                                </NavItem>
                            )}
                        </>
                    )}

                    {!opportunity.isInterested() && opportunity.hasSubmittedProposal() && !isSupplier && (
                        <NavItem active={selectedTab === "Supporting Documents"}>
                            <NavLink className={TB.mapClass({
                                clickable: selectedTab !== "Supporting Documents",
                                active: selectedTab === "Supporting Documents"
                            })} onClick={() => onSetActiveTab("Supporting Documents")}>
                                Supporting Documents
                            </NavLink>
                        </NavItem>
                    )}

                    {opportunity.canMessageAndAcceptInterviews() && (
                        <NavItem active={selectedTab === "Interviews"}>
                            <NavLink className={TB.mapClass({
                                clickable: selectedTab !== "Interviews",
                                active: selectedTab === "Interviews"
                            })} onClick={() => onSetActiveTab("Interviews")}>
                                Interviews
                                <Badge count={opportunity.proposed_meetings_count || 0} className="ml-1" />
                            </NavLink>
                        </NavItem>
                    )}

                    {opportunity.canMessageAndAcceptInterviews() && !isSupplier && (
                        <NavItem active={selectedTab === "Messages"}>
                            <NavLink className={TB.mapClass({
                                clickable: selectedTab !== "Messages",
                                active: selectedTab === "Messages"
                            })} onClick={() => onSetActiveTab("Messages")}>
                                Messages
                                <Badge count={opportunity.unread_messages_count || 0} className="ml-1" />

                            </NavLink>
                        </NavItem>
                    )}

                </Nav>
            </div>

            <div className="proposal-overview overflow-auto">

                <div className="proposal-tabs-section">

                    <TabContent activeTab={selectedTab} className="proposal-tab-content">

                        {isTeamOpp && (
                            <TabPane tabId="Team">
                                <div className={TB.mapClass("p-0 md:py-4", {
                                    disabled: isDisabled,
                                })}>
                                    <TeamScopeTab onUpdate={changeProps} />
                                </div>
                            </TabPane>
                        )}
                        {isTeamOpp && (
                            <TabPane tabId="Role & Payout">
                                <div className={TB.mapClass("p-0 md:py-4", {
                                    disabled: isDisabled,
                                })}>
                                    <TeamMemberPayoutEditTab onUpdate={updateOpp} />
                                </div>
                            </TabPane>
                        )}

                        {isIndividualOpp && (
                            <TabPane tabId="My Match Score">
                                <div className={TB.mapClass("p-0 md:py-4", {
                                    disabled: isDisabled,
                                })}>
                                    <MatchScoreEditTab onUpdate={changeProps} />
                                </div>
                            </TabPane>
                        )}


                        {!isSupplier && (
                            <TabPane tabId="Cover Letter">
                                <div className={TB.mapClass("p-0 md:px-6 md:py-4", {
                                    disabled: isDisabled,
                                })}>
                                    <CoverLetterEditTab onUpdate={updateOpp} />
                                </div>
                            </TabPane>
                        )}

                        {isIndividualOpp && !isSupplier && (
                            <>
                                {opportunity.isFixedRetainer() && (
                                    <TabPane tabId="Pricing">
                                        <div className={TB.mapClass("p-0 md:px-6 md:py-4", {
                                            disabled: isDisabled,
                                        })}>
                                            <FixedRetainerPayoutEditTab onUpdate={updateOpp} />
                                        </div>
                                    </TabPane>
                                )}

                                {opportunity.isFixedFee() && (
                                    <TabPane tabId="Payout">
                                        <div className={TB.mapClass("p-0 md:px-6 md:py-4", {
                                            disabled: isDisabled,
                                        })}>
                                            <FixedFeePayoutEditTab onUpdate={updateOpp} />
                                        </div>
                                    </TabPane>
                                )}

                                {opportunity.isTimeMaterials() && (
                                    <TabPane tabId="Availability & Rate">
                                        <div className={TB.mapClass("p-0 md:px-6 md:py-4", {
                                            disabled: isDisabled,
                                        })}>
                                            <AvailabilityRateEditTab onUpdate={updateOpp} />
                                        </div>
                                    </TabPane>
                                )}
                            </>
                        )}

                        {!opportunity.isInterested() && opportunity.hasSubmittedProposal() && !isSupplier && (
                            <TabPane tabId="Supporting Documents">
                                <div className={TB.mapClass("p-0 md:px-6 md:py-4", {
                                    disabled: isDisabled
                                })}>
                                    <DocumentsEditTab onUpdate={updateOpp} />
                                </div>
                            </TabPane>
                        )}

                        {opportunity.canMessageAndAcceptInterviews() && (
                            <TabPane tabId="Interviews">
                                <div className={TB.mapClass({
                                    disabled: isDisabled
                                })}>
                                    {selectedTab === "Interviews" && (
                                        <ManageOppMeetings />
                                    )}
                                </div>
                            </TabPane>
                        )}


                        {opportunity.canMessageAndAcceptInterviews() && !isSupplier && (
                            <TabPane tabId="Messages" className="mx-[-1rem] my-[-1rem]">
                                <div className="proposal-messages">
                                    {selectedTab === "Messages" && (
                                        <MessagesTab
                                            opportunity={opportunity}
                                            candidate={opportunity.job_candidate}
                                            onFetchSuccess={() => {
                                                changeProps(
                                                    Opportunity.make({
                                                        ...opportunity,
                                                        unread_messages_count: 0
                                                    })
                                                )
                                            }}
                                        />
                                    )}
                                </div>
                            </TabPane>
                        )}
                    </TabContent>

                </div>
            </div>
        </div >
    )
};

OpportunityProposal.propTypes = {
    disabled: PropTypes.bool,
    opportunity: PropTypes.any,
    onRequest: PropTypes.any
};

export default OpportunityProposal;